import React, { FunctionComponent, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import {
  AdminApi,
  AdminUserPermission,
  AdminUserRetrieve,
  EnvironmentPermissionsEnum,
  OrganizationPermissionsEnum,
  PermissionTypeEnum,
  AdminOrganizationDetails,
  DistributionPlatformPermissionEnum,
  AdminIssuerDetailPermissionsEnum,
  ProductPermissionEnum,
  AdminUserPermissionsListOrderingEnum,
  AdminUserPermissionsValuesRetrieveFieldEnum,
} from 'src/api';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { I18NContext } from 'src/ui/i18n/provider';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import DeletePermissionModal from '../delete-permission-modal';
import { useCurrentUserSelector } from 'src/core/auth/hooks';
import { currentUserHasManageEnvironmentPermission } from 'src/apps/issuer/helpers/userPermissions';
import useApiCall from 'src/hooks/use-api-call';
import { currentOrganizationHasManageOrganizationPermission } from 'src/apps/issuer/helpers/organizationPermissions';
import { Search } from 'src/ui/molecules/filtering';

export interface IssuerPermissionsTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  permissions?: AdminUserPermission[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  setPermissionModalOpen: (val: AdminUserPermission) => void;
  hasPermission: boolean;
  userOrganization: AdminOrganizationDetails | null;
  userId?: string;
}

const PermissionsTable: FunctionComponent<IssuerPermissionsTableProps> = (props) => {
  const {
    className,
    loading,
    ordering,
    onOrderBy,
    permissions,
    hasPermission,
    paginationProps,
    setPermissionModalOpen,
    userOrganization,
    userId,
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const { activeLocale } = useContext(I18NContext);
  const { currentUser } = useCurrentUserSelector();

  const getPermissionTypeOptions = useMemo(() => {
    const options = [];

    if (currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) {
      options.push({
        name: translate('permissionTypes.environment'),
        id: 'ENVIRONMENT',
      });
    }

    if (
      currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) ||
      (userOrganization && currentOrganizationHasManageOrganizationPermission(userOrganization))
    ) {
      options.push(
        {
          name: translate('permissionTypes.organization'),
          id: 'ORGANIZATION',
        },
        {
          name: translate('permissionTypes.product'),
          id: 'PRODUCT',
        },
        {
          name: translate('permissionTypes.issuer'),
          id: 'ISSUER',
        },
        {
          name: translate('permissionTypes.distributionPlatform'),
          id: 'DISTRIBUTION_PLATFORM',
        },
      );
    }

    return options;
  }, [currentUser?.permissions, userOrganization, translate]);

  const getPermissionCodeFilterOptions = useMemo(() => {
    const permissionsCodes = [];

    if (currentUserHasManageEnvironmentPermission(currentUser?.permissions || [])) {
      permissionsCodes.push({
        values: Object.values(EnvironmentPermissionsEnum),
        prefixKey: 'permissionTypes.environment',
      });
    }
    if (
      currentUserHasManageEnvironmentPermission(currentUser?.permissions || []) ||
      (userOrganization && currentOrganizationHasManageOrganizationPermission(userOrganization))
    ) {
      permissionsCodes.push(
        {
          values: Object.values(OrganizationPermissionsEnum),
          prefixKey: 'permissionTypes.organization',
        },
        {
          values: Object.values(ProductPermissionEnum),
          prefixKey: 'permissionTypes.product',
        },
        {
          values: Object.values(AdminIssuerDetailPermissionsEnum),
          prefixKey: 'permissionTypes.issuer',
        },
        {
          values: Object.values(DistributionPlatformPermissionEnum),
          prefixKey: 'permissionTypes.distributionPlatform',
        },
      );
    }

    const options = permissionsCodes.flatMap(({ values, prefixKey }) => {
      const prefixLabel = translate(prefixKey);

      return values.map((permissionCode) => ({
        id: permissionCode,
        name: `${prefixLabel} - ${translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`)}`,
      }));
    });

    // sort alphabetically
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }, [currentUser?.permissions, userOrganization, translate]);

  const getValidForOptions = useCallback(
    (permissionsObjectsOptions: string[]) => {
      const regex = /\{\{(.*?)\}\}/g;
      const a = permissionsObjectsOptions.map((str) => {
        const replacedString = str.replace(regex, (match, capturedWord) =>
          translate(`permissionTypes.${snakeToCamelCaseWithNumbers(capturedWord.toLowerCase())}`),
        );
        return {
          value: str,
          label: replacedString,
          name: replacedString,
        };
      });

      return a;
    },
    [translate],
  );

  return (
    <div className={cx('users-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={permissions}
        paginationProps={paginationProps}
        loading={loading}
        hasPermission={hasPermission}
        searchComponent={
          <Search
            input="search"
            defaultField="search"
            label={translate('filters.labels.userPermissionId')}
            name="search"
          />
        }
        cells={[
          {
            title: <Translate name="issuerPermissionsTable.type" />,
            render: ({ permissionType }) => translate(`permissionTypes.${snakeToCamelCaseWithNumbers(permissionType)}`),
            orderingFieldName: AdminUserPermissionsListOrderingEnum.PERMISSION_TYPE_ASC,
            type: CellTypeEnum.text,
            filterName: 'permissionType',
            getFilterData: () => {
              return getPermissionTypeOptions;
            },
          },
          {
            title: <Translate name="issuerPermissionsTable.permission" />,
            render: ({ permissionCode }) => translate(`permissionNames.${snakeToCamelCaseWithNumbers(permissionCode)}`),
            orderingFieldName: AdminUserPermissionsListOrderingEnum.PERMISSION_CODE_ASC,
            type: CellTypeEnum.text,
            filterName: 'permissionCode',
            getFilterData: () => {
              return getPermissionCodeFilterOptions;
            },
          },
          {
            title: <Translate name="issuerPermissionsTable.validFor" />,
            render: ({ permissionType, validForName }) => {
              return permissionType === PermissionTypeEnum.ENVIRONMENT ? (
                <Translate name="permissionTypes.environment" />
              ) : (
                validForName
              );
            },
            orderingFieldName: AdminUserPermissionsListOrderingEnum.VALID_FOR_NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'validForName',
            searchFieldName: AdminUserPermissionsValuesRetrieveFieldEnum.VALID_FOR_NAME,
            getFilterData: (params: any) => adminApi.adminUserPermissionsValuesRetrieve({ ...params, userId: userId }),
            processFilterData: (data: string[]) => getValidForOptions(data),
          },
          {
            title: <Translate name="issuerPermissionsTable.actions" />,
            render: (permission) => (
              <ActionButtons align="center" spacingTop="none">
                <Button
                  variant="secondary"
                  size="xsmall"
                  onClick={() => setPermissionModalOpen(permission)}
                  disabled={!permission.canRemove}
                >
                  <Translate name="common.remove" />
                </Button>
              </ActionButtons>
            ),
            width: activeLocale === 'de' ? 122 : 110,
          },
        ]}
      />
    </div>
  );
};

interface PermissionsTableProps {
  title?: ReactNode;
  filters?: ReactNode;
  forceDataLoad?: boolean;
  setForceDataLoad: (val: boolean) => void;
  user: AdminUserRetrieve;
  userId: string;
  skipFilterUrlSync?: boolean;
  setDeletePermissionSuccessModalOpen: (value: boolean) => void;
  hasPermission: boolean;
  userOrganization: AdminOrganizationDetails | null;
}

const IssuerPermissionsTable: FunctionComponent<PermissionsTableProps> = WithDataRecord<AdminApi, AdminUserPermission>(
  AdminApi,
)(
  ({
    data,
    ordering,
    onOrderBy,
    loading,
    paginationProps,
    setForceDataLoad,
    setDeletePermissionSuccessModalOpen,
    hasPermission,
    user,
    userId,
    userOrganization,
  }) => {
    const [permissionModalOpen, setPermissionModalOpen] = useState<AdminUserPermission | undefined>();

    return (
      <>
        <PermissionsTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          permissions={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          setPermissionModalOpen={setPermissionModalOpen}
          userOrganization={userOrganization}
          userId={userId}
        />
        {permissionModalOpen && (
          <DeletePermissionModal
            onDelete={() => {
              setForceDataLoad(true);
              setPermissionModalOpen(undefined);
              setDeletePermissionSuccessModalOpen(true);
            }}
            onClose={() => setPermissionModalOpen(undefined)}
            permission={permissionModalOpen}
            userEmail={user.email}
          />
        )}
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminUserPermissionsList({
      offset,
      limit,
      ordering: ordering as AdminUserPermissionsListOrderingEnum,
      ...props,
      userId: [props.userId],
    });
  },
);

export default IssuerPermissionsTable;
